import { DataTypeEnum } from "../constants/data-type.enum";

/**
 * Model for the Table column that are possible and visible.
 */
export class TableColumn {
    columnName!: string;
    transTag!: string;
    noOfColumns!: number;
    columnWidth?: number;
    showMobile: boolean = false;
    type!: DataTypeEnum;
    visible: boolean = true;
}

/**
 * Model for the Table column picker for with columns can been picked.
 */
export class TableColumnPicker {
    name!: string;
    isSelected: boolean = true;
    disabled: boolean = false;
    hidden: boolean = false;
}

/**
 * A helper function that can create columns, usefull to add non value columns (like 'actions')
 * @param propertyName property to display in the column, NOT TYPED
 * @param HeaderTranslationKey translation key (?)
 * @param dataType if any parsing of the value needs to be done, you can change this enum for default parsing/formatting
 * @param noOfColumns if not all columns need to be the same size use this to allow columns to be larger
 * @param showMobile whether this column should be displayed for mobile tables
 * @returns a TableColumn model with all properties set
 */
export function CreateNonTypedColumn(propertyName: string, HeaderTranslationKey: string, dataType: DataTypeEnum = DataTypeEnum.General, noOfColumns: number = 2, showMobile: boolean = false) {

    let column = new TableColumn();
    column.columnName = propertyName.toString();
    column.transTag = HeaderTranslationKey;
    column.noOfColumns = noOfColumns;
    column.showMobile = showMobile;
    column.type = dataType;

    return column;
}
/**
 * A helper function that can create DEFAULT columns relying on the defaults set
 * THIS function allows us to later change defaults without having to go past ALL functions, but we can just set the defaults here if needed
 * @param propertyName property to display in the column, by typed name
 * @param HeaderTranslationKey translation key
 * @returns a TableColumn model with DEFAULT properties set
 */
export function CreateDefaultBasicColumn<T>(propertyName: keyof T, HeaderTranslationKey: string) {
    return CreateColumn<T>(propertyName, HeaderTranslationKey);
}


/**
 * A helper function that can create columns
 * @param propertyName property to display in the column, by typed name
 * @param HeaderTranslationKey translation key (?)
 * @param dataType if any parsing of the value needs to be done, you can change this enum for default parsing/formatting
 * @param noOfColumns if not all columns need to be the same size use this to allow columns to be larger
 * @param showMobile whether this column should be displayed for mobile tables
 * @param visible wether to show this column by default or not. Default set to true.
 * @returns a TableColumn model with all properties set
 */
export function CreateColumn<T>(propertyName: keyof T, HeaderTranslationKey: string, dataType: DataTypeEnum = DataTypeEnum.General, noOfColumns: number = 2, showMobile: boolean = false, visible: boolean = true, ) {

    let column = new TableColumn();
    column.columnName = propertyName.toString();
    column.transTag = HeaderTranslationKey;
    column.noOfColumns = noOfColumns;
    column.showMobile = showMobile;
    column.type = dataType;
    column.visible = visible;

    return column;
}

/**
 * A helper function that can create columns pickers
 * @param propertyName property to display in the column picker, by typed name
 * @param defaultSelected if the picker has to been selected by default or not
 * @param disabled if the picker has to been disabled by default or not
 * @returns a TableColumnPicker model with all properties set
 */
export function CreateColumnPicker<T>(propertyName: keyof T, defaultSelected: boolean = true, disabled: boolean = false, hidden: boolean = false): TableColumnPicker {
    let columnFilter = new TableColumnPicker();
    columnFilter.name = propertyName.toString();
    columnFilter.isSelected = defaultSelected;
    columnFilter.disabled = disabled;
    columnFilter.hidden = hidden;
    return columnFilter;
}
/**
 * A helper function that can create DEFAULT column pickers relying on the defaults set
 * THIS function allows us to later change defaults without having to go past ALL functions, but we can just set the defaults here if needed
 * @param propertyName property to display in the column, by typed name
 * @returns a TableColumn model with DEFAULT properties set
 */
export function CreateColumnPickerAlwaysSelectable<T>(propertyName: keyof T): TableColumnPicker {
    return CreateColumnPicker(propertyName, true, false);
}

/**
 * A helper function that can create column pickers that is selected but also disabled.
 * THIS function allows us to later change defaults without having to go past ALL functions, but we can just set the defaults here if needed
 * @param propertyName property to display in the column, by typed name
 * @returns a TableColumn model with DEFAULT properties set
 */
export function CreateColumnPickerSelectedAndDisabled<T>(propertyName: keyof T): TableColumnPicker {
    return CreateColumnPicker(propertyName, true, true);
}

/**
 * A helper function that can create column pickers that isn't selected and enabled
 * THIS function allows us to later change defaults without having to go past ALL functions, but we can just set the defaults here if needed
 * @param propertyName property to display in the column, by typed name
 * @returns a TableColumn model with DEFAULT properties set
 */
export function CreateColumnPickerDeSelectedAndDisabled<T>(propertyName: keyof T): TableColumnPicker {
    return CreateColumnPicker(propertyName, false, true);
}

/**
 * A helper function that can create column pickers that is selected and enabled
 * THIS function allows us to later change defaults without having to go past ALL functions, but we can just set the defaults here if needed
 * @param propertyName property to display in the column, by typed name
 * @returns a TableColumn model with DEFAULT properties set
 */
export function CreateColumnPickerDeSelectedAndEnabled<T>(propertyName: keyof T): TableColumnPicker {
    return CreateColumnPicker(propertyName, false, false);
}

/**
 * A helper function that can create column pickers that is selected and enabled
 * THIS function allows us to later change defaults without having to go past ALL functions, but we can just set the defaults here if needed
 * @param propertyName property to display in the column, by typed name
 * @returns a TableColumn model with DEFAULT properties set
 */
export function CreateColumnPickerSelectedAndHidden<T>(propertyName: keyof T): TableColumnPicker {
    return CreateColumnPicker(propertyName, true, false, true);
}

